import React, { useState, useEffect } from "react";
import { Button, Grid, LinearProgress, Box, Typography, Tooltip, ClickAwayListener, Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";
import CurrencyTextField from "@unicef/material-ui-currency-textfield";
import { Club, CompanyBudgetDefault, getCompanyBudgetDefault, getCompanyBudgets, CompanyBudget, updateCompanyBudgetDefault } from "api/clubs";
import { Info as InfoIcon } from "@material-ui/icons";
import { Subscription } from "api/company";
import { View } from "features/global/reducer";
import { useSnackbar } from "notistack";
import { DefaultTable } from "components";
import { Column } from "material-table";
import moment from "moment";
import { useMounted } from "utils/hooks";
import FirstWarningMsgDialog from "./FirstWarningMsgDialog";
import WarningMsgDialog from "./WarningMsgDialog";
import CustomerDetails from "./CustomerDetails";
import AttentionBanner from "./AttentionBanner";
import { ErrorTypes, formatter } from "../../../utils/helpers";
import i18n from "./i18n";

// prop types
interface CompanySettingsTabProps {
    club: Club,
    subscription: Subscription | null,
    onNavigateTo: ({ view, tab }: { view: View, tab?: number }) => void,
}

// member club settings tab
const CompanySettingsTab = ({ club, subscription, onNavigateTo }: CompanySettingsTabProps) => {
    // ui state
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );
    const [companyBudgetDefault, setCompanyBudgetDefault] = useState<CompanyBudgetDefault>();
    const [isLoading, setLoading] = useState(false);
    const [monthlyCompanyMemberDefaultBudget, setMonthlyCompanyMemberDefaultBudget] = useState(0);
    const [monthlyCompanyTotalBudget, setMonthlyCompanyTotalBudget] = useState(0);
    const [companyClubId, setCompanyClubId] = useState("");
    const [showWarningDialog, setWarningDialog] = useState(false);
    const [showFirstWarningDialog, setShowFirstWarningDialog] = useState(false);
    const [openMonthlyBudgetToolTip, setOpenMonthlyBudgetToolTip] = useState(false);
    const [openTotalBudgetToolTip, setOpenTotalBudgetToolTip] = useState(false);
    const [isChangedMonthlyBudget, setIsChangedMonthlyBudget] = useState(false);
    const [applyToCurrentMonth, setApplyToCurrentMonth] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);

    const handleTooltipCloseForMonthlyBudget = () => {
        setOpenMonthlyBudgetToolTip(false);
    };
    const handleTooltipOpenForMonthlyBudget = () => {
        setOpenMonthlyBudgetToolTip(true);
    };

    const handleTooltipCloseForTotalBudget = () => {
        setOpenTotalBudgetToolTip(false);
    };
    const handleTooltipOpenForTotalBudget = () => {
        setOpenTotalBudgetToolTip(true);
    };

    const getBudgets = async (clubId: string): Promise<void> => {
        try {
            setLoading(true);
            setCompanyClubId(clubId);
            if (club.clubStatus !== "demo") {
                const defaultBudget = await getCompanyBudgetDefault({ clubId });
                setCompanyBudgetDefault(defaultBudget);
                const initialMonthlyCompanyMemberDefaultBudget = (defaultBudget?.monthlyCompanyMemberDefaultBudgetCents !== undefined ? (defaultBudget?.monthlyCompanyMemberDefaultBudgetCents) / 100 : 0);
                setMonthlyCompanyMemberDefaultBudget(initialMonthlyCompanyMemberDefaultBudget);

                const initialMonthlyCompanyTotalBudget = (defaultBudget?.monthlyCompanyTotalBudgetCents !== undefined ? (defaultBudget?.monthlyCompanyTotalBudgetCents) / 100 : 0);
                setMonthlyCompanyTotalBudget(initialMonthlyCompanyTotalBudget);
            }
        } catch (error) {
            displayError(error);
        } finally {
            setLoading(false);
        }
    };

    const preSaveAction = () => {
        if (isChangedMonthlyBudget) {
            setWarningDialog(false);
            setShowFirstWarningDialog(true);
        } else {
            setApplyToCurrentMonth(false);
            saveAction(true, false);
        }
    };

    const firstWarningAction = async (applyToCurrentMonthFlag: boolean) => {
        setApplyToCurrentMonth(applyToCurrentMonthFlag);
        setShowFirstWarningDialog(false);
        const monthlyCompanyMemberDefaultBudgetCent = monthlyCompanyMemberDefaultBudget * 100;
        const monthlyCompanyMemberDefaultBudgetCents = (companyBudgetDefault?.monthlyCompanyMemberDefaultBudgetCents !== undefined ? companyBudgetDefault?.monthlyCompanyMemberDefaultBudgetCents : 0);

        if (monthlyCompanyMemberDefaultBudgetCent >= monthlyCompanyMemberDefaultBudgetCents) {
            saveAction(true, applyToCurrentMonthFlag);
        } else {
            setWarningDialog(true);
        }
    };

    // api request
    const saveAction = async (applySaveAction: boolean, applyToCurrentMonthFlag: boolean) => {
        if (!applySaveAction) {
            setWarningDialog(false);
        } else {
            try {
                setLoading(true);
                await updateCompanyBudgetDefault({
                    clubId: companyClubId,
                    monthlyCompanyMemberDefaultBudgetCents: (monthlyCompanyMemberDefaultBudget * 100),
                    monthlyCompanyTotalBudgetCents: (monthlyCompanyTotalBudget * 100),
                    applyToCurrentMonth: applyToCurrentMonthFlag,
                });
                refreshMembersTable();
                getBudgets(companyClubId);
                enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
            } catch (error: any) {
                if (error.type === ErrorTypes.USED_BUDGET_EXCEEDS_MONTHLY_TOTAL_BUDGET) {
                    setShowErrorDialog(true);
                } else if (error.type === ErrorTypes.CLUB_IN_DEMO_MODE) {
                    enqueueSnackbar(i18n.demoModeErrorMsg, { variant: "error" });
                } else {
                    displayError(error);
                }
            } finally {
                setLoading(false);
                setWarningDialog(false);
                setIsChangedMonthlyBudget(false);
            }
        }
    };

    useEffect(() => {
        if (club) {
            getBudgets(club.id);
        }
    }, [club]);

    // refresh (-> remote data)
    const tableRef = React.useRef<any>(null);
    const isMounted = useMounted();
    const refreshMembersTable = () => {
        if (!isMounted) return; // skip on initial material-table fetch
        if (tableRef.current) tableRef.current.onQueryChange(); // execute query again
    };

    useEffect(refreshMembersTable, [club]);

    const tableColumns: Column<CompanyBudget>[] = [
        {
            editable: "never",
            field: "monthDate",
            render: (rowData: CompanyBudget) => moment(rowData.monthDate).format("MMMM YYYY"),
            title: i18n.month,
        },
        {
            editable: "never",
            field: "totalBudgetCents",
            render: (rowData: CompanyBudget) => formatter.format(rowData.totalBudgetCents / 100),
            title: i18n.totalBudget,
        },
        {
            editable: "never",
            field: "usedBudgetCents",
            render: (rowData: CompanyBudget) => formatter.format(rowData.usedBudgetCents / 100),
            title: i18n.usedBudget,
        },
    ];

    const defaultData = {
        data: [],
        page: 0,
        totalCount: 0,
    };

    // render
    return (
        <>
            <Box style={{ alignItems: "center", display: "flex", flexDirection: "column", padding: "16px" }}>
                {subscription === null && (
                    <AttentionBanner club={club} onNavigateTo={onNavigateTo} />
                )}

                <Grid container>

                    <Grid item xs={12} md={12}>
                        <Typography align="left" variant="h6">{i18n.budgetSettings}</Typography>
                    </Grid>
                    <Grid container>
                        <Grid item xs={12}>
                            {isLoading && <LinearProgress />}
                        </Grid>
                        <Grid item xs={11} md={5} className="companySettingsLbl">
                            <Typography align="left" variant="subtitle2">
                                {i18n.defaultMonthlyMemberBudget}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} md={1}>
                            <ClickAwayListener onClickAway={handleTooltipCloseForMonthlyBudget}>
                                <div>
                                    <Tooltip
                                        PopperProps={{
                                            disablePortal: true,
                                        }}
                                        onClose={handleTooltipCloseForMonthlyBudget}
                                        open={openMonthlyBudgetToolTip}
                                        placement="bottom-end"
                                        disableFocusListener
                                        disableHoverListener
                                        disableTouchListener
                                        title={i18n.warningMsgFordefaultMonthlyCompanyBudgetChange}
                                        arrow
                                    >
                                        <InfoIcon onClick={handleTooltipOpenForMonthlyBudget} color="secondary" fontSize="small" style={{ margin: "18px 0" }} />
                                    </Tooltip>
                                </div>
                            </ClickAwayListener>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Grid item xs={12}>
                                <CurrencyTextField
                                    currencySymbol="€"
                                    decimalCharacter=","
                                    digitGroupSeparator="."
                                    fullWidth
                                    label=""
                                    helperText=""
                                    maximumValue="1000000"
                                    minimumValue="0"
                                    value={companyBudgetDefault?.monthlyCompanyMemberDefaultBudgetCents !== undefined ? (companyBudgetDefault?.monthlyCompanyMemberDefaultBudgetCents) / 100 : 0}
                                    variant="outlined"
                                    onChange={(e: any, value: number) => {
                                        setMonthlyCompanyMemberDefaultBudget(value); setIsChangedMonthlyBudget(true);
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={11} md={5} className="companySettingsLbl">
                            <Typography align="left" variant="subtitle2">
                                {i18n.monthlyCompanyTotalBudget}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} md={1}>
                            <ClickAwayListener onClickAway={handleTooltipCloseForTotalBudget}>
                                <div>
                                    <Tooltip
                                        PopperProps={{
                                            disablePortal: true,
                                        }}
                                        onClose={handleTooltipCloseForTotalBudget}
                                        open={openTotalBudgetToolTip}
                                        placement="bottom-end"
                                        disableFocusListener
                                        disableHoverListener
                                        disableTouchListener
                                        title={i18n.monthlyCompanyTotalBudgetInfoDesc}
                                        arrow
                                    >
                                        <InfoIcon onClick={handleTooltipOpenForTotalBudget} color="secondary" fontSize="small" style={{ margin: "18px 0" }} />
                                    </Tooltip>
                                </div>
                            </ClickAwayListener>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Grid item xs={12} md={12}>
                                <CurrencyTextField
                                    currencySymbol="€"
                                    decimalCharacter=","
                                    digitGroupSeparator="."
                                    fullWidth
                                    label=""
                                    helperText=""
                                    maximumValue="1000000"
                                    minimumValue="0"
                                    value={companyBudgetDefault?.monthlyCompanyTotalBudgetCents !== undefined ? (companyBudgetDefault?.monthlyCompanyTotalBudgetCents) / 100 : 0}
                                    variant="outlined"
                                    onChange={(e: any, value: number) => {
                                        setMonthlyCompanyTotalBudget(value); setIsChangedMonthlyBudget(true);
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid item xs={12} md={12} style={{ textAlign: "center" }}>
                            <Button
                                color="primary"
                                disabled={isLoading || !isChangedMonthlyBudget}
                                className="btn"
                                variant="contained"
                                onClick={() => preSaveAction()}
                            >
                                {i18n.save}
                            </Button>
                        </Grid>

                    </Grid>
                </Grid>
                {subscription !== null && (
                    <Grid container>
                        <Grid item xs={12} md={12}>
                            <Typography align="left" variant="h6">{i18n.budgetHistory}</Typography>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            {club.clubStatus !== "demo" && (
                                <DefaultTable
                                    data={(query) => new Promise((resolve) => {
                                        getCompanyBudgets({
                                            clubId: club.id,
                                            limit: query.pageSize,
                                            offset: query.pageSize * query.page,
                                        }).then((response) => {
                                            resolve({
                                                data: response.data,
                                                totalCount: response.totalCount,
                                                page: query.page,
                                            });
                                        }).catch((error) => {
                                            displayError(error);
                                            resolve(defaultData);
                                        });
                                    })}
                                    options={{ search: false, showTitle: false, toolbar: false }}
                                    columns={tableColumns}
                                    tableRef={tableRef}
                                    isLoading={isLoading}
                                />
                            )}
                        </Grid>
                    </Grid>
                )}
                {subscription !== null && (
                    <CustomerDetails club={club} />
                )}
            </Box>

            {/* Warning message */}
            <FirstWarningMsgDialog showFirstWarningDialog={showFirstWarningDialog} onCloseFirstDialog={() => setShowFirstWarningDialog(false)} firstWarningAction={firstWarningAction} />
            {(monthlyCompanyMemberDefaultBudget * 100 < (companyBudgetDefault?.monthlyCompanyMemberDefaultBudgetCents !== undefined ? companyBudgetDefault?.monthlyCompanyMemberDefaultBudgetCents : 0)) && (
                <WarningMsgDialog show={showWarningDialog} applyToCurrentMonth={applyToCurrentMonth} onClose={() => setWarningDialog(false)} saveAction={saveAction} />
            )}

            <Dialog open={showErrorDialog} onClose={() => setShowErrorDialog(false)}>
                <DialogTitle>{i18n.alert}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography>{i18n.usedBudgetExceedsMonthlyTotalBudgetErrorMsg}</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setShowErrorDialog(false)}>{i18n.close}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CompanySettingsTab;
